import { useEffect } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';

// Value format: "dev.azure.com/{org}" e.g. "dev.azure.com/mediwareis"
const AZURE_HOST_ORG_ANNOTATION = 'dev.azure.com/host-org';
const AZURE_PROJECT_ANNOTATION = 'dev.azure.com/project';

// Renders nothing. Attaches a document-level capture listener while the CI/CD
// tab is mounted so that "VIEW LOGS" clicks open Azure DevOps in a new tab
// instead of fetching the full build log (~22MB) which freezes the browser.
//
// EntityAzurePipelinesContent is a Backstage routable extension and cannot be
// wrapped inside another component — this sibling approach avoids that constraint.
export const AzurePipelinesLogInterceptor = () => {
  const { entity } = useEntity();
  // "dev.azure.com/mediwareis" → "mediwareis"
  const hostOrg = entity.metadata.annotations?.[AZURE_HOST_ORG_ANNOTATION];
  const org = hostOrg?.split('/').pop();
  const project = entity.metadata.annotations?.[AZURE_PROJECT_ANNOTATION];

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const button = target.closest('button');
      if (!button) return;

      const buttonText = button.textContent
        ?.replace(/\s+/g, ' ')
        .trim()
        .toUpperCase();
      if (buttonText !== 'VIEW LOGS') return;

      // Capture-phase stopPropagation prevents the plugin's modal from opening.
      event.stopPropagation();

      if (!org || !project) return;

      // Build ID is in the first <td> of the same table row.
      const row = button.closest('tr');
      const buildId = row?.querySelector('td:first-child')?.textContent?.trim();
      if (!buildId || !/^\d+$/.test(buildId)) return;

      window.open(
        `https://dev.azure.com/${org}/${encodeURIComponent(
          project,
        )}/_build/results?buildId=${buildId}&view=logs`,
        '_blank',
        'noopener,noreferrer',
      );
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [org, project]);

  return null;
};
